import { CurrentSong as SongType, Language } from '@yleisradio/areena-types';
import { useUILanguage } from 'hooks/useUILanguage';
import React, { useEffect, useState } from 'react';
import { getCurrentSongForChannel } from 'services/areena-api/fetchers';
import useSWR from 'swr';
import { getParentChannel } from 'utils/channel';
import { getRandomInt } from 'utils/random';
import styles from './CurrentSong.module.scss';
import { Time } from 'components/Time';
import classNames from 'classnames';
import { useTranslation } from 'hooks/useTranslation';

type RequestKey = ['song', channelId: string, language: Language];

async function fetcher([
  ,
  channelId,
  language,
]: RequestKey): Promise<SongType | null> {
  return getCurrentSongForChannel(channelId, language);
}

type Props = {
  channelId: string;
  isParentLoading: boolean;
};

export const CurrentSong: React.FC<Props> = ({
  channelId,
  isParentLoading,
}) => {
  const [lastChecked, setLastChecked] = useState(Date.now());

  const language = useUILanguage();

  const t = useTranslation();

  const parentChannelId = getParentChannel(channelId);

  const {
    data: song,
    isLoading: isSongLoading,
    mutate: mutateSong,
  } = useSWR(['song', channelId, language] satisfies RequestKey, fetcher);
  const {
    data: parentChannelSong,
    isLoading: isParentChannelSongLoading,
    mutate: mutateParentChannelSong,
  } = useSWR(
    parentChannelId
      ? (['song', parentChannelId, language] satisfies RequestKey)
      : null,
    fetcher
  );

  const isLoading =
    isParentLoading || isSongLoading || isParentChannelSongLoading;
  const { performer, title, startTime, endTime } =
    song || parentChannelSong || {};

  useEffect(() => {
    const refetch = () => {
      setLastChecked(Date.now());
      mutateSong();
      mutateParentChannelSong();
    };

    let refetchAfter = endTime
      ? Date.parse(endTime) - Date.now() + getRandomInt(3_000, 30_000) // Add random delay to spread the load on API
      : 0;

    if (isNaN(refetchAfter) || refetchAfter <= 0) refetchAfter = 30_000;

    const timeout = setTimeout(refetch, refetchAfter);

    return () => clearTimeout(timeout);
  }, [
    endTime,
    lastChecked,
    mutateParentChannelSong,
    mutateSong,
    setLastChecked,
  ]);

  if (!title && !isLoading) return null;

  if (isLoading)
    return <div className={classNames(styles.root, styles.loading)} />;

  return (
    <div className={styles.root}>
      <span className={styles.nowPlaying}>{t('nowPlaying')}</span>
      <Time date={startTime} />
      {' '}
      {performer}
      {performer && ' - '}
      {title}
    </div>
  );
};
